import { getOperatorsHttpOrigin, type OperatorsUser } from "./operators";

export const OPERATORS_AUTH_EXPIRED_MESSAGE =
  "Your Operators session expired. Sign in again to continue.";
export const OPERATORS_AUTH_EXPIRED_WS_CLOSE_CODE = 4401;

export type OperatorsAuthTool = {
  icon: string;
  name: string;
  route: string;
  slug: string;
};

export type OperatorsAuthSession = {
  expiresAt: string;
  flags?: Record<string, boolean>;
  isSuperAdmin?: boolean;
  permissions?: string[];
  roles?: string[];
  tools?: OperatorsAuthTool[];
  user: OperatorsUser;
};

type AuthExpiredListener = (message: string) => void;
type AuthRefreshedListener = (session: OperatorsAuthSession | null) => void;

const authExpiredListeners = new Set<AuthExpiredListener>();
const authRefreshedListeners = new Set<AuthRefreshedListener>();

export class OperatorsAuthExpiredError extends Error {
  constructor(message = OPERATORS_AUTH_EXPIRED_MESSAGE) {
    super(message);
    this.name = "OperatorsAuthExpiredError";
  }
}

export function isOperatorsAuthExpiredError(error: unknown): error is OperatorsAuthExpiredError {
  return error instanceof OperatorsAuthExpiredError;
}

export function onOperatorsAuthExpired(listener: AuthExpiredListener): () => void {
  authExpiredListeners.add(listener);
  return () => {
    authExpiredListeners.delete(listener);
  };
}

export function onOperatorsAuthSessionRefreshed(listener: AuthRefreshedListener): () => void {
  authRefreshedListeners.add(listener);
  return () => {
    authRefreshedListeners.delete(listener);
  };
}

export function notifyOperatorsAuthExpired(message = OPERATORS_AUTH_EXPIRED_MESSAGE): void {
  for (const listener of authExpiredListeners) {
    try {
      listener(message);
    } catch {
      // Keep notifying the rest of the app.
    }
  }
}

export function notifyOperatorsAuthSessionRefreshed(session: OperatorsAuthSession | null): void {
  for (const listener of authRefreshedListeners) {
    try {
      listener(session);
    } catch {
      // Keep notifying the rest of the app.
    }
  }
}

export function throwOperatorsAuthExpired(message = OPERATORS_AUTH_EXPIRED_MESSAGE): never {
  notifyOperatorsAuthExpired(message);
  throw new OperatorsAuthExpiredError(message);
}

function reachabilityErrorMessage(): string {
  const browserOrigin =
    typeof window === "undefined" ? "this browser origin" : window.location.origin;
  return `Could not reach the Operators control plane at ${getOperatorsHttpOrigin()} from ${browserOrigin}. Check VITE_OPERATORS_HTTP_URL, ALLOWED_UI_ORIGINS, and SESSION_COOKIE_SAME_SITE.`;
}

export async function readOperatorsAuthErrorMessage(response: Response): Promise<string> {
  try {
    const parsed = (await response.json()) as {
      message?: string | string[];
      error?: string;
    };
    if (typeof parsed.message === "string" && parsed.message.length > 0) {
      return parsed.message;
    }
    if (Array.isArray(parsed.message) && parsed.message.length > 0) {
      return parsed.message.join(", ");
    }
    if (typeof parsed.error === "string" && parsed.error.length > 0) {
      return parsed.error;
    }
  } catch {
    // Fall through to the status text fallback.
  }

  return response.statusText || "Request failed";
}

export async function fetchOperators(input: string | URL, init?: RequestInit): Promise<Response> {
  try {
    return await fetch(input, init);
  } catch {
    throw new Error(reachabilityErrorMessage());
  }
}

function parseAuthSession(payload: unknown): OperatorsAuthSession | null {
  if (!payload || typeof payload !== "object") {
    return null;
  }

  const candidate = payload as Partial<OperatorsAuthSession>;
  if (!candidate.user || typeof candidate.expiresAt !== "string") {
    return null;
  }

  const session: OperatorsAuthSession = {
    expiresAt: candidate.expiresAt,
    user: candidate.user,
  };
  if (typeof candidate.isSuperAdmin === "boolean") {
    session.isSuperAdmin = candidate.isSuperAdmin;
  }
  if (Array.isArray(candidate.permissions)) {
    session.permissions = candidate.permissions.filter(
      (value): value is string => typeof value === "string",
    );
  }
  if (Array.isArray(candidate.roles)) {
    session.roles = candidate.roles.filter((value): value is string => typeof value === "string");
  }
  if (candidate.flags && typeof candidate.flags === "object" && !Array.isArray(candidate.flags)) {
    const flags: Record<string, boolean> = {};
    for (const [key, value] of Object.entries(candidate.flags)) {
      if (typeof value === "boolean") {
        flags[key] = value;
      }
    }
    session.flags = flags;
  }
  if (Array.isArray(candidate.tools)) {
    session.tools = candidate.tools.flatMap((tool) => {
      if (!tool || typeof tool !== "object") {
        return [];
      }
      const entry = tool as Partial<OperatorsAuthTool>;
      if (
        typeof entry.slug !== "string" ||
        typeof entry.name !== "string" ||
        typeof entry.route !== "string" ||
        typeof entry.icon !== "string"
      ) {
        return [];
      }
      return [
        {
          icon: entry.icon,
          name: entry.name,
          route: entry.route,
          slug: entry.slug,
        },
      ];
    });
  }
  return session;
}

export async function fetchCurrentOperatorsSession(options?: {
  notifyOnUnauthorized?: boolean;
}): Promise<OperatorsAuthSession | null> {
  const response = await fetchOperators(`${getOperatorsHttpOrigin()}/api/v1/auth/me`, {
    credentials: "include",
  });
  if (response.status === 401) {
    if (options?.notifyOnUnauthorized ?? true) {
      throwOperatorsAuthExpired();
    }
    return null;
  }
  if (!response.ok) {
    throw new Error(await readOperatorsAuthErrorMessage(response));
  }

  return parseAuthSession(await response.json());
}

/** Local-development sign-in. Fails unless the control plane sets OPERATORS_DEV_AUTH_EMAIL. */
export async function createDevSession(): Promise<OperatorsAuthSession> {
  const response = await fetchOperators(`${getOperatorsHttpOrigin()}/api/v1/auth/dev-session`, {
    method: "POST",
    credentials: "include",
  });
  if (!response.ok) {
    throw new Error(await readOperatorsAuthErrorMessage(response));
  }

  const session = parseAuthSession(await response.json());
  if (!session) {
    throw new Error("Dev sign-in did not return an Operators session.");
  }
  return session;
}

export async function exchangeGoogleIdToken(idToken: string): Promise<OperatorsAuthSession> {
  const response = await fetchOperators(`${getOperatorsHttpOrigin()}/api/v1/auth/google/exchange`, {
    method: "POST",
    headers: {
      "content-type": "application/json",
    },
    credentials: "include",
    body: JSON.stringify({
      idToken,
    }),
  });
  if (!response.ok) {
    throw new Error(await readOperatorsAuthErrorMessage(response));
  }

  const session = parseAuthSession(await response.json());
  if (!session) {
    throw new Error("Authentication did not return an Operators session.");
  }
  return session;
}

export async function refreshOperatorsSession(): Promise<OperatorsAuthSession> {
  const response = await fetchOperators(`${getOperatorsHttpOrigin()}/api/v1/auth/refresh`, {
    method: "POST",
    credentials: "include",
  });
  if (response.status === 401) {
    throwOperatorsAuthExpired();
  }
  if (!response.ok) {
    throw new Error(await readOperatorsAuthErrorMessage(response));
  }

  const session = parseAuthSession(await response.json());
  if (!session) {
    throw new Error("Session refresh did not return an Operators session.");
  }
  notifyOperatorsAuthSessionRefreshed(session);
  return session;
}

export async function logoutOperator(): Promise<void> {
  await fetchOperators(`${getOperatorsHttpOrigin()}/api/v1/auth/logout`, {
    method: "POST",
    credentials: "include",
  });
}
